package fr.frux.feature.treelist

import androidx.compose.ui.graphics.vector.ImageVector
import fr.frux.core.designsystem.components.harvestPeriodLabel
import fr.frux.core.designsystem.components.untilEndOfLabel
import fr.frux.core.designsystem.format.formatDistance
import fr.frux.core.designsystem.icons.glyphFor
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.maps.Location
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.distanceMetersFrom
import fr.frux.shared.domain.model.needsCheck
import fr.frux.shared.presentation.TreeSighting
import fr.frux.shared.presentation.resolveTreeStatus
import fr.frux.shared.presentation.treeSighting
import fr.frux.shared.presentation.treeSightingLabel
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/**
 * Traduit un arbre du domaine en item affiché par l'écran « Tous les arbres », statut résolu et
 * libellé de saison compact compris.
 *
 * [harvests] vient du référentiel espèces (peut être vide si le référentiel est indisponible ou
 * si l'espèce n'a pas de fenêtres déclarées) ; [currentMonth] et [now] sont injectés par
 * l'appelant pour rester testable sans horloge système.
 */
@OptIn(ExperimentalTime::class)
fun Tree.toTreeListItem(
    harvests: List<HarvestWindow>,
    currentMonth: Int,
    reference: Location?,
    now: Instant,
): TreeListItem {
    val distanceText = distanceMetersFrom(reference)?.let { "à ${formatDistance(it)}" } ?: "Distance inconnue"
    val season = seasonLabel(harvests, currentMonth)
    val sighting = treeSighting(lastObservedAt, photoUrl, now)
    val sightingText = sightingLabel(sighting, lastObservedAt?.toEpochMilliseconds(), now.toEpochMilliseconds())
    return TreeListItem(
        id = id,
        name = species.commonNameFr,
        meta = listOfNotNull(distanceText, season).joinToString(" · "),
        status = resolveTreeStatus(status, harvests, currentMonth, lastObservedAt, now),
        glyph = glyphFor(species.scientificName, harvestPart?.name),
        scientificName = species.scientificName,
        photoUrl = photoUrl,
        // Même signal qu'à la carte et à la fiche (ADR 0015, #143) : identification déclarée à
        // confirmer, ou contestée par les pairs.
        needsCheck = needsCheck,
        sightingLabel = sightingText,
    )
}

/**
 * Mention de fraîcheur de la vignette (#420, ADR 0025) : même dérivation que la fiche
 * ([fr.frux.shared.presentation.treeSighting]) et mêmes mots ([treeSightingLabel], seul endroit qui
 * les décide, #427).
 *
 * `NEVER_SEEN` rend une chaîne vide, jamais affichée (cf. `TreeCard`) : sur une liste, l'immense
 * majorité des arbres n'a pas d'observation, et l'ADR 0025 écarte déjà le marqueur de carte pour
 * cette même raison (« un signe porté par la majorité ne porte aucune information ») — la vignette
 * ne garde que les deux niveaux datés, qui distinguent vraiment un arbre d'un autre. La fiche, elle,
 * répond à une question posée en ouvrant l'arbre : elle garde les trois.
 */
private fun sightingLabel(
    sighting: TreeSighting,
    lastObservedAtMillis: Long?,
    nowMillis: Long,
): String =
    if (sighting == TreeSighting.NEVER_SEEN) "" else treeSightingLabel(sighting, lastObservedAtMillis, nowMillis)

/**
 * Libellé de saison compact : "Jusqu'à fin <mois>" si une fenêtre est en cours (la plus éloignée
 * de sa fin si plusieurs se chevauchent), sinon la période de la prochaine fenêtre à venir (la
 * plus proche en mois, wrap compris). Null si aucune fenêtre. Même règle que
 * `TreeDetailMapper.seasonLabel`, dupliquée localement (le mapping de statut, lui, est factorisé
 * dans `fr.frux.shared.presentation`, #87).
 */
private fun seasonLabel(
    harvests: List<HarvestWindow>,
    currentMonth: Int,
): String? {
    if (harvests.isEmpty()) return null

    val ongoing = harvests.filter { it.isInSeason(currentMonth) }
    if (ongoing.isNotEmpty()) {
        val furthest = ongoing.maxBy { monthsForwardWrap(currentMonth, it.monthEnd) }
        return untilEndOfLabel(furthest.monthEnd)
    }

    val next = harvests.minBy { monthsForwardWrap(currentMonth, it.monthStart) }
    return harvestPeriodLabel(next.monthStart, next.monthEnd)
}

/** Nombre de mois (0..11) pour aller de [from] à [to] en avançant, avec wrap décembre→janvier. */
private fun monthsForwardWrap(
    from: Int,
    to: Int,
): Int = ((to - from) + 12) % 12

/** Item affiché par l'écran « Tous les arbres », statut déjà résolu (cf. [toTreeListItem]). */
data class TreeListItem(
    val id: Long,
    val name: String,
    val meta: String,
    val status: FruxStatus,
    val glyph: ImageVector? = null,
    /** Pour teinter la pastille comme le marqueur (`treeMarkerTint`). */
    val scientificName: String = "",
    val photoUrl: String? = null,
    val needsCheck: Boolean = false,
    /** Passage humain devant l'arbre (#420, ADR 0025) : rendu par `TreeCard` sous [meta]. */
    val sightingLabel: String = "",
)
